import * as React from "react"
import { cn } from "@/lib/utils"

export interface GlassCardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "default" | "elevated" | "flat" | "interactive"
  blur?: "none" | "sm" | "md" | "lg" | "xl"
  gradient?: boolean
}

const GlassCard = React.forwardRef<HTMLDivElement, GlassCardProps>(
  ({ className, variant = "default", blur = "md", gradient = false, ...props }, ref) => {
    const blurClasses = {
      none: "",
      sm: "backdrop-blur-sm",
      md: "backdrop-blur-md",
      lg: "backdrop-blur-lg",
      xl: "backdrop-blur-xl",
    }

    const variantClasses = {
      default: "bg-white/60 dark:bg-slate-900/60 border-border dark:border-white/15 shadow-xl shadow-black/5 dark:shadow-black/30",
      elevated: "bg-white/70 dark:bg-slate-900/70 border-border dark:border-white/20 shadow-2xl shadow-black/10 dark:shadow-black/40",
      flat: "bg-white/40 dark:bg-slate-900/40 border-border/60 dark:border-white/10",
      interactive: "bg-white/60 dark:bg-slate-900/60 border-border dark:border-white/15 hover:bg-white/80 dark:hover:bg-slate-900/80 hover:border-indigo-500/50 dark:hover:border-white/30 hover:shadow-2xl transition-all duration-300 cursor-pointer",
    }

    return (
      <div
        ref={ref}
        className={cn(
          "relative rounded-lg border",
          blurClasses[blur],
          variantClasses[variant],
          gradient && "before:absolute before:inset-0 before:rounded-lg before:bg-gradient-glass before:opacity-50",
          "transform transition-all duration-300",
          className
        )}
        {...props}
      />
    )
  }
)
GlassCard.displayName = "GlassCard"

const GlassCardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-6", className)}
    {...props}
  />
))
GlassCardHeader.displayName = "GlassCardHeader"

const GlassCardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "text-xl font-semibold leading-none tracking-tight text-slate-800 dark:text-slate-100",
      className
    )}
    {...props}
  />
))
GlassCardTitle.displayName = "GlassCardTitle"

const GlassCardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-slate-600 dark:text-slate-400", className)}
    {...props}
  />
))
GlassCardDescription.displayName = "GlassCardDescription"

const GlassCardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
GlassCardContent.displayName = "GlassCardContent"

const GlassCardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
GlassCardFooter.displayName = "GlassCardFooter"

export { GlassCard, GlassCardHeader, GlassCardFooter, GlassCardTitle, GlassCardDescription, GlassCardContent }

